import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PostApi from "../../../api/PostApi";
import PageNavigation from "../../pageNavigation/PageNavigation";
import {
    getPostBaseUrl,
    getPostCategoryName,
    getPostLabelName,
    getPostNicknm,
} from "../../../constant/PostClass";
import { getRemovedTagContent } from "../../../util/StringUtil";
import { resolvePostProfileImg } from "../../../util/ProfileUtil";
import { TodayTimestampDisplay } from "../../common/TimestampDisplay";
import { shouldShowWriterMedal } from "../../../util/MembershipUtil";
import { resolvePostThumbnailUrl } from "../../../util/ImageService";

const ProfileThumbnail = ({ src, alt }) => {
    const [show, setShow] = useState(!!src);

    useEffect(() => {
        setShow(!!src);
    }, [src]);

    if (!src || !show) return null;

    return (
        <span className="profile-thumb">
            <img src={src} alt={alt} onError={() => setShow(false)} />
        </span>
    );
};

const MyPostListItem = ({ post, onCopy }) => {
    const navigate = useNavigate();
    const nicknm = getPostNicknm(post);
    const labelName =
        getPostLabelName(post.post_category) ||
        getPostCategoryName(post) ||
        post.post_category ||
        "";
    const profileImg = resolvePostProfileImg(post);
    const thumbnailUrl = resolvePostThumbnailUrl(post);
    const detailUrl = `${getPostBaseUrl(post)}/post/detail/${post.post_id}`;
    const isNotice = post.post_category === "PCA01";

    const handleCardClick = () => {
        navigate(detailUrl);
    };

    const handleCopyClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onCopy(post);
    };

    return (
        <article
            className={`board-list-item${isNotice ? " is-notice" : ""}`}
            onClick={handleCardClick}
            onKeyDown={(e) => {
                if (e.key === "Enter") handleCardClick();
            }}
            role="button"
            tabIndex={0}
        >
            <div className="board-list-col">
                <span className="category-list-label">{labelName}</span>
            </div>

            <div className="board-list-content">
                <div className="board-list-title">
                    <strong>{post.post_title}</strong>
                </div>
                <p className="board-list-desc">{getRemovedTagContent(post.content)}</p>

                <div className="board-list-meta">
                    <div className="board-list-stats-item">
                        <span>{nicknm}</span>
                        {shouldShowWriterMedal(post) && <i className="icon icon-medal"></i>}
                    </div>
                    <div className="board-list-stats-item">
                        <span>
                            <TodayTimestampDisplay timestamp={post.post_reg_date} />
                        </span>
                    </div>
                    <div className="board-list-stats-item">
                        <i className="icon icon-heart"></i>
                        <span>{post.post_like_cnt}</span>
                    </div>
                    <div className="board-list-stats-item">
                        <i className="icon icon-chat"></i>
                        <span>{post.post_comment_cnt}</span>
                    </div>
                    <div className="board-list-stats-item">
                        <i className="icon icon-eye"></i>
                        <span>{post.post_visit}</span>
                    </div>
                </div>

                <button
                    type="button"
                    className="btn btn-secondary btn-sm mypage-post-copy-btn"
                    onClick={handleCopyClick}
                >
                    복사 재등록
                </button>
            </div>

            <div className="board-list-author">
                <ProfileThumbnail src={profileImg} alt={`${nicknm} 프로필`} />
                <span>{nicknm}</span>
                {shouldShowWriterMedal(post) && <i className="icon icon-medal"></i>}
            </div>

            <div className="board-list-stats">
                <div className="board-list-stats-item">
                    <span>
                        <TodayTimestampDisplay timestamp={post.post_reg_date} />
                    </span>
                </div>
                <div className="board-list-stats-item">
                    <i className="icon icon-heart"></i>
                    <span>{post.post_like_cnt}</span>
                </div>
                <div className="board-list-stats-item">
                    <i className="icon icon-chat"></i>
                    <span>{post.post_comment_cnt}</span>
                </div>
                <div className="board-list-stats-item">
                    <i className="icon icon-eye"></i>
                    <span>{post.post_visit}</span>
                </div>
            </div>
            <div className="board-list-thumb">
                {thumbnailUrl ? (
                    <img src={thumbnailUrl} alt="썸네일" />
                ) : null}
            </div>
        </article>
    );
};

const MyPost = () => {
    const navigate = useNavigate();
    const [totalCnt, setTotalCnt] = useState(0);
    const [posts, setPosts] = useState([]);
    const [page, setPage] = useState(1);

    const getPosts = async (props = {}) => {
        const { newPage = 1 } = props;
        const rslt = await PostApi.getAllPosts(newPage, false, true);
        if (rslt?.posts) setPosts(rslt.posts);
    };

    const handlePageChage = (newPage) => {
        if (!newPage) return;
        setPage(newPage);
        getPosts({ newPage });
    };

    const handlePostCopy = (post) => {
        navigate(`/post/write/${post.post_id}?postType=${post.post_type}&copy=true`, {
            state: { post },
        });
    };

    useEffect(() => {
        PostApi.getAllPosts(page, true, true).then((rslt) => {
            if (rslt?.totalCnt != null) setTotalCnt(rslt.totalCnt);
        });
    }, []);

    return (
        <section className="mypage-container">
            <div className="section-area">
                <div className="section-header">
                    <div className="search-bar">
                        <input
                            type="text"
                            placeholder="검색어를 입력해주세요"
                            className="search-url"
                            disabled
                            aria-hidden="true"
                            tabIndex={-1}
                        />
                        <button type="button" className="btn btn-primary" disabled tabIndex={-1}>
                            <i className="icon icon-search-bold"></i>
                        </button>
                    </div>
                </div>

                <div className="section-content">
                    <div className="board-list">
                        {posts.map((post) => (
                            <MyPostListItem
                                key={post.post_id}
                                post={post}
                                onCopy={handlePostCopy}
                            />
                        ))}
                    </div>
                </div>

                <PageNavigation totalCount={totalCnt} showCount={30} onChange={handlePageChage} />
            </div>
        </section>
    );
};

export default MyPost;
